/**
 * 메인 랜딩: fullPage.js (jQuery) + 우측 페이저 + GSAP / SplitType 섹션 전환
 * (Lenis는 가상 스크롤과 충돌 → 인스턴스가 있으면 제거)
 */
import SplitType from "split-type";

type LenisLike = {
  destroy?: () => void;
  stop?: () => void;
};

/** 전역 스크립트 GSAP 최소 타입 */
type GsapLike = {
  set: (t: unknown, v: Record<string, unknown>) => void;
  to: (t: unknown, v: Record<string, unknown>) => { kill: () => void };
  fromTo: (
    t: unknown,
    fromVars: Record<string, unknown>,
    toVars: Record<string, unknown>
  ) => { kill: () => void };
  timeline: (v?: Record<string, unknown>) => GsapTimelineLike;
  killTweensOf: (t: unknown) => void;
};

type GsapTimelineLike = {
  kill: () => void;
  to: (
    t: unknown,
    v: Record<string, unknown>,
    position?: number | string
  ) => GsapTimelineLike;
};

function getGsap(): GsapLike | null {
  const g = (window as unknown as { gsap?: GsapLike }).gsap;
  return g ?? null;
}

function destroyLenisIfPresent(): void {
  const w = window as unknown as { lenis?: LenisLike };
  const instance = w.lenis;
  if (!instance) return;
  try {
    instance.destroy?.();
  } catch {
    /* noop */
  }
  try {
    instance.stop?.();
  } catch {
    /* noop */
  }
  delete w.lenis;
  document.documentElement.classList.remove("lenis", "lenis-smooth", "lenis-scrolling", "lenis-stopped");
}

function getSections(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(".no-main__section"));
}

function setActiveDot(dots: HTMLButtonElement[], activeIdx: number): void {
  dots.forEach((dot, i) => {
    const on = i === activeIdx;
    dot.classList.toggle("no-main__pager-dot--active", on);
    dot.setAttribute("aria-current", on ? "true" : "false");
  });
}

function setActivePagerItems(items: HTMLElement[], activeIdx: number): void {
  items.forEach((el, i) => {
    el.classList.toggle("no-main__pager-item--active", i === activeIdx);
  });
}

function getPagerLabelText(dot: HTMLButtonElement): string {
  const raw = dot.dataset.pagerLabel?.trim();
  if (raw) return raw;
  return dot.getAttribute("aria-label")?.trim() ?? "";
}

function tryPlayVideo(video: HTMLVideoElement): void {
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute("muted", "");
  video.setAttribute("playsinline", "");
  const p = video.play();
  if (p && typeof p.catch === "function") {
    p.catch(() => {
      /* autoplay can be blocked until user gesture */
    });
  }
}

function ensureMainVideosPlayback(root: HTMLElement): void {
  const videos = Array.from(root.querySelectorAll<HTMLVideoElement>(".no-main__video"));
  if (!videos.length) return;

  videos.forEach((video) => {
    if (video.readyState >= 2) {
      tryPlayVideo(video);
      return;
    }
    video.addEventListener("loadeddata", () => tryPlayVideo(video), { once: true });
  });

  const onFirstGesture = (): void => {
    videos.forEach((video) => tryPlayVideo(video));
    window.removeEventListener("pointerdown", onFirstGesture);
    window.removeEventListener("touchstart", onFirstGesture);
    window.removeEventListener("keydown", onFirstGesture);
  };

  window.addEventListener("pointerdown", onFirstGesture, { once: true });
  window.addEventListener("touchstart", onFirstGesture, { once: true });
  window.addEventListener("keydown", onFirstGesture, { once: true });
}

interface PreparedSection {
  section: HTMLElement;
  content: HTMLElement;
  titleSplit: SplitType;
  textSplit: SplitType;
  media: HTMLElement | null;
}

function prepareSectionSplits(section: HTMLElement): PreparedSection | null {
  const content = section.querySelector<HTMLElement>(".no-main__content");
  const title = section.querySelector<HTMLElement>(".no-main__title");
  const text = section.querySelector<HTMLElement>(".no-main__text");
  const media = section.querySelector<HTMLElement>(".no-main__media");
  if (!content || !title || !text) return null;

  const titleSplit = new SplitType(title, {
    types: "chars",
    tagName: "span",
  });
  const textSplit = new SplitType(text, {
    types: "lines",
    tagName: "span",
  });

  return { section, content, titleSplit, textSplit, media };
}

let transitionTween: { kill: () => void } | null = null;

/** GSAP filter 트윈이 kill 되면 인라인 filter 가 남아 화면이 흐릿하게 고정될 수 있음 */
function clearSectionFilters(gsap: GsapLike, p: PreparedSection): void {
  const chars = p.titleSplit.chars ?? [];
  const lines = p.textSplit.lines ?? [];
  gsap.set(p.content, { clearProps: "filter" });
  if (chars.length) gsap.set(chars, { clearProps: "filter" });
  if (lines.length) gsap.set(lines, { clearProps: "filter" });
}

function killTransitionTweens(gsap: GsapLike, prepared: Map<HTMLElement, PreparedSection>): void {
  if (transitionTween) {
    transitionTween.kill();
    transitionTween = null;
  }
  for (const p of prepared.values()) {
    gsap.killTweensOf(p.content);
    if (p.media) gsap.killTweensOf(p.media);
    const chars = p.titleSplit.chars ?? [];
    const lines = p.textSplit.lines ?? [];
    if (chars.length) gsap.killTweensOf(chars);
    if (lines.length) gsap.killTweensOf(lines);
    clearSectionFilters(gsap, p);
  }
}

export function initMainFullPage(root: HTMLElement | null = document.querySelector(".no-main")): void {
  if (!root) return;
  ensureMainVideosPlayback(root);

  const sections = getSections(root);
  if (sections.length === 0) return;

  const $ = window.jQuery;
  if (!$ || typeof $.fn.fullpage !== "function") {
    return;
  }

  const gsap = getGsap();
  if (!gsap) return;

  destroyLenisIfPresent();

  const dots = Array.from(root.querySelectorAll<HTMLButtonElement>(".no-main__pager-dot"));
  const pager = root.querySelector<HTMLElement>(".no-main__pager");
  const pagerItems = Array.from(root.querySelectorAll<HTMLElement>(".no-main__pager-item"));
  const pagerLive = root.querySelector<HTMLElement>("#no-main-pager-live");
  const scrollHints = Array.from(root.querySelectorAll<HTMLButtonElement>(".no-main__scroll-hint"));

  const footerSectionIndex1Based = sections.length;

  const preparedMap = new Map<HTMLElement, PreparedSection>();
  for (const sec of sections) {
    if (sec.classList.contains("no-main__section--footer")) continue;
    const p = prepareSectionSplits(sec);
    if (p) preparedMap.set(sec, p);
  }

  for (const p of preparedMap.values()) {
    const chars = p.titleSplit.chars ?? [];
    const lines = p.textSplit.lines ?? [];
    gsap.set([...chars, ...lines], { opacity: 0, yPercent: 22 });
    gsap.set(p.content, { opacity: 0, y: 28 });
    if (p.media) {
      gsap.set(p.media, { scale: 1.08, transformOrigin: "50% 50%" });
    }
  }

  const syncDots = (index1Based: number): void => {
    if (pager) {
      const onFooter = index1Based === footerSectionIndex1Based;
      pager.classList.toggle("no-main__pager--hidden", onFooter);
      pager.setAttribute("aria-hidden", onFooter ? "true" : "false");
    }
    if (index1Based < footerSectionIndex1Based && dots.length) {
      const idx0 = index1Based - 1;
      setActiveDot(dots, idx0);
      if (pagerItems.length) {
        setActivePagerItems(pagerItems, idx0);
      }
      const text = getPagerLabelText(dots[idx0]);
      if (pagerLive && text) {
        pagerLive.textContent = text;
      }
    }
  };

  const runEnter = (sectionEl: HTMLElement): void => {
    const p = preparedMap.get(sectionEl);
    if (!p) return;

    killTransitionTweens(gsap, preparedMap);

    const chars = p.titleSplit.chars ?? [];
    const lines = p.textSplit.lines ?? [];

    clearSectionFilters(gsap, p);
    gsap.set([...chars, ...lines], { opacity: 0, yPercent: 22 });
    gsap.set(p.content, { opacity: 0, y: 26 });
    if (p.media) {
      gsap.set(p.media, { scale: 1.1 });
    }

    const tl = gsap.timeline({
      defaults: { ease: "power4.out" },
      onComplete: () => {
        transitionTween = null;
      },
    });
    transitionTween = tl;

    if (p.media) {
      tl.to(
        p.media,
        { scale: 1, duration: 1.75, ease: "power2.inOut" },
        0
      );
    }

    tl.to(p.content, { opacity: 1, y: 0, duration: 0.72, ease: "power3.out" }, 0.06);
    tl.to(
      chars,
      {
        opacity: 1,
        yPercent: 0,
        duration: 0.82,
        stagger: { each: 0.034, from: "start" },
        ease: "power3.out",
      },
      0.14
    );
    tl.to(
      lines,
      {
        opacity: 1,
        yPercent: 0,
        duration: 0.92,
        stagger: { each: 0.09, from: "start" },
        ease: "power3.out",
      },
      0.26
    );
  };

  const runLeave = (sectionEl: HTMLElement): void => {
    const p = preparedMap.get(sectionEl);
    if (!p) return;

    killTransitionTweens(gsap, preparedMap);

    const chars = p.titleSplit.chars ?? [];
    const lines = p.textSplit.lines ?? [];

    const tw = gsap.to(p.content, {
      opacity: 0,
      y: -18,
      duration: 0.42,
      ease: "power3.in",
      onComplete: () => {
        gsap.set([...chars, ...lines], { opacity: 0, yPercent: 18 });
        if (p.media) gsap.set(p.media, { scale: 1.06 });
      },
    });
    transitionTween = tw;
  };

  const jq = window.jQuery as unknown as (sel: unknown) => { get: (i: number) => HTMLElement | undefined };
  const sectionCount = sections.length;

  /** 구버전 공유 링크 `#fp4` → `?fp=4` (해시 제거) */
  const migrateHashToFpQuery = (): void => {
    const m = /^#fp(\d+)$/i.exec(window.location.hash);
    if (!m) return;
    const u = new URL(window.location.href);
    u.searchParams.set("fp", m[1]);
    u.hash = "";
    history.replaceState(null, "", u.pathname + u.search);
  };

  const parseFpFromUrl = (): number | null => {
    const raw = new URLSearchParams(window.location.search).get("fp");
    if (raw === null || raw === "") return null;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) return null;
    return n;
  };

  /** 스크롤 위치를 `?fp=` 로 반영 (1번째 섹션은 쿼리 없이 깔끔하게) */
  const syncFpQueryUrl = (index1Based: number): void => {
    const u = new URL(window.location.href);
    u.hash = "";
    if (index1Based <= 1) {
      u.searchParams.delete("fp");
    } else {
      u.searchParams.set("fp", String(index1Based));
    }
    const qs = u.searchParams.toString();
    const next = qs ? `${u.pathname}?${qs}` : u.pathname;
    history.replaceState(null, "", next);
  };

  migrateHashToFpQuery();

  const initialFp = parseFpFromUrl();
  /** `?fp=2` 이상 딥링크: fullPage가 afterRender 전에 afterLoad(1)를 호출하므로 URL 동기화를 잠시 막음 */
  const needsDeepLink =
    initialFp !== null && initialFp > 1 && initialFp <= sectionCount;
  let allowUrlSync = !needsDeepLink;

  $(root).fullpage({
    sectionSelector: ".no-main__section",
    /** 비우면 fullPage가 `#fpN` 해시를 쓰지 않음 → 아래에서 `?fp=` 로만 동기화 */
    anchors: [],
    navigation: false,
    scrollBar: true,
    autoScrolling: true,
    verticalCentered: false,
    recordHistory: false,
    fitToSection: true,
    scrollingSpeed: 1000,
    afterRender: function () {
      if (!needsDeepLink || initialFp === null) return;
      allowUrlSync = true;
      $.fn.fullpage.silentMoveTo(initialFp);
    },
    afterLoad: function (this: unknown, _anchorLink: string, index: number) {
      if (allowUrlSync) syncFpQueryUrl(index);
      syncDots(index);
      const sectionEl = jq(this).get(0);
      if (!sectionEl) return;
      if (needsDeepLink && !allowUrlSync && index === 1) {
        return;
      }
      runEnter(sectionEl);
    },
    onLeave: function (this: unknown, _leavingIndex: number, _destinationIndex: number, _direction: string) {
      const sectionEl = jq(this).get(0);
      if (!sectionEl) return;
      runLeave(sectionEl);
    },
  });

  window.addEventListener("popstate", () => {
    const n = parseFpFromUrl();
    if (n !== null && n >= 1 && n <= sectionCount) {
      $.fn.fullpage.moveTo(n);
    } else {
      $.fn.fullpage.moveTo(1);
    }
  });

  dots.forEach((dot, i) => {
    dot.addEventListener("click", () => {
      $.fn.fullpage.moveTo(i + 1);
    });
  });

  scrollHints.forEach((btn) => {
    btn.addEventListener("click", () => {
      $.fn.fullpage.moveSectionDown();
    });
  });
}
